import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, User as UserIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function MapView() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      const loc = JSON.parse(savedLocation);
      setUserLocation(loc);
      fetchPosts(loc);
    } else {
      const defaultLoc = { lat: 12.9716, lng: 77.5946 };
      setUserLocation(defaultLoc);
      fetchPosts(defaultLoc);
    }
  }, []);

  const fetchPosts = async (loc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/posts`, {
        params: {
          latitude: loc.lat,
          longitude: loc.lng,
          radius: 20000,
          category: 'all'
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      free: '#10b981',
      sale: '#3b82f6',
      service: '#8b5cf6',
      help: '#f97316',
      emergency: '#ef4444',
      network: '#ec4899',
      community: '#eab308'
    };
    return colors[category] || '#6b7280';
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 z-50">
        <div className="flex items-center gap-4 px-4 py-4">
          <button onClick={() => navigate('/home')}>
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Post Locations</h1>
            <p className="text-sm text-gray-500">{posts.length} posts nearby</p>
          </div>
        </div>
      </header>

      {/* Map */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        ) : userLocation ? (
          <MapContainer
            center={[userLocation.lat, userLocation.lng]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* User Location */}
            <Circle
              center={[userLocation.lat, userLocation.lng]}
              radius={5000}
              pathOptions={{
                fillColor: '#f97316',
                fillOpacity: 0.1,
                color: '#f97316',
                weight: 2,
                opacity: 0.5
              }}
            />

            {/* User Marker */}
            <Marker position={[userLocation.lat, userLocation.lng]}>
              <Popup>
                <div className="text-center">
                  <p className="font-semibold text-orange-600">Your Location</p>
                  <p className="text-xs text-gray-600">You are here</p>
                </div>
              </Popup>
            </Marker>

            {/* Post Markers */}
            {posts.map((post) => {
              const icon = L.divIcon({
                className: 'custom-marker',
                html: `
                  <div style="position: relative;">
                    <div style="
                      width: 36px;
                      height: 36px;
                      border-radius: 50%;
                      border: 3px solid ${getCategoryColor(post.category)};
                      background: white;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                    ">
                      <svg style="width: 20px; height: 20px; color: ${getCategoryColor(post.category)};" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                    <div style="
                      position: absolute;
                      bottom: -6px;
                      left: 50%;
                      transform: translateX(-50%);
                      width: 0;
                      height: 0;
                      border-left: 6px solid transparent;
                      border-right: 6px solid transparent;
                      border-top: 6px solid ${getCategoryColor(post.category)};
                    "></div>
                  </div>
                `,
                iconSize: [36, 42],
                iconAnchor: [18, 42],
                popupAnchor: [0, -42]
              });

              return (
                <Marker 
                  key={post.id} 
                  position={[post.latitude, post.longitude]}
                  icon={icon}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm">{post.user_name}</h4>
                          <p className="text-xs text-gray-500">{post.distance} km away</p>
                        </div>
                      </div>
                      <h3 className="font-bold text-gray-900 mb-1">{post.title}</h3>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{post.description}</p>
                      <div className="flex items-center justify-between">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${getCategoryColor(post.category)}20`,
                            color: getCategoryColor(post.category)
                          }}
                        >
                          {post.category}
                        </span>
                        <button
                          onClick={() => navigate(`/post/${post.id}`)}
                          className="text-xs text-orange-600 font-medium hover:underline"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        ) : null}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
          <h3 className="text-xs font-semibold text-gray-700 mb-2">Categories</h3>
          <div className="space-y-1">
            {[
              { name: 'Free', color: '#10b981' },
              { name: 'Sale', color: '#3b82f6' },
              { name: 'Service', color: '#8b5cf6' },
              { name: 'Help', color: '#f97316' },
              { name: 'Emergency', color: '#ef4444' },
              { name: 'Network', color: '#ec4899' },
            ].map((cat) => (
              <div key={cat.name} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full border-2" 
                  style={{ borderColor: cat.color, backgroundColor: 'white' }}
                />
                <span className="text-xs text-gray-600">{cat.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
